import { lazy, Suspense, type ReactNode } from "react"
import { Router, Route, Switch, Redirect, useLocation } from "wouter"
import { EnvBanner } from "@rawa/ui/EnvBanner"
import { ErrorBoundary } from "./components/ErrorBoundary"
import { Layout } from "./components/Layout"
import { PortraitOnly } from "./components/PortraitOnly"
import { ProtectedRoute } from "./components/ProtectedRoute"
import { AdminProtectedRoute } from "./components/AdminProtectedRoute"
import {
  FeedbackFormPage,
  FeedbackWelcomePage,
  InvitationGate,
  NotFoundPage,
  ThankYouPage,
} from "./modules/feedback"
import {
  KioskWelcomePage,
  KioskLoginPage,
  SessionListPage,
  SessionParticipantsPage,
  LocationSettingsPage,
} from "./modules/kiosk"
import { KioskProtectedRoute } from "./components/KioskProtectedRoute"
import { IdentityChallengePage } from "./modules/challenge/pages/IdentityChallengePage"
import { SyncStatusPage } from "./modules/admin/pages/SyncStatusPage"
import { ServiceTypesListPage } from "./modules/admin/pages/ServiceTypesListPage"
import { ProgramQuestionsPage } from "./modules/admin/pages/ProgramQuestionsPage"
import { ProgramConfigPage } from "./modules/admin/pages/ProgramConfigPage"
import { RolloutPage } from "./modules/admin/pages/RolloutPage"
import { KioskDevicesListPage } from "./modules/admin/pages/KioskDevicesListPage"
import { ModulesListPage } from "./modules/admin/pages/ModulesListPage"
import { NewModulePage } from "./modules/admin/pages/NewModulePage"
import { ModuleCanvasPage } from "./modules/admin/pages/ModuleCanvasPage"
import { SessionTemplatesListPage } from "./modules/admin/pages/SessionTemplatesListPage"
import { NewSessionTemplatePage } from "./modules/admin/pages/NewSessionTemplatePage"
import { SessionTemplatePage } from "./modules/admin/pages/SessionTemplatePage"
import { LoginPage } from "./modules/auth/pages/LoginPage"
import { DiagnosticsPage } from "./modules/diagnostics/pages/DiagnosticsPage"
import { paths, pathHelpers } from "./paths"
import { FeedbackLayout } from "./components/FeedbackLayout"

// Lazy + code-split: the Form.io builder (~2MB) is the ONLY route that pulls
// @formio/*, and this dynamic import is the only path to it — so it lands in its
// own chunk and never downloads for kiosk/feedback users. Keep it a dynamic
// import (never a static import) or Form.io leaks into the main bundle.
const FormBuilderPage = lazy(() =>
  import("./modules/admin/pages/FormBuilderPage").then((m) => ({
    default: m.FormBuilderPage,
  })),
)

// Unguarded on purpose: the page's own device-authenticated load redirects to
// setup on a 401, so a route guard here would only add a second, serialised
// credential check ahead of it.
function SessionListRoute() {
  const [, navigate] = useLocation()
  return (
    <SessionListPage
      onInvitationCreated={(id) => navigate(pathHelpers.toKioskFeedback(id))}
    />
  )
}

function FeedbackWelcomeRoute({ params }: { params: { id: string } }) {
  return (
    <InvitationGate invitationId={params.id}>
      <FeedbackWelcomePage invitationId={params.id} />
    </InvitationGate>
  )
}

function FeedbackFormRoute({ params }: { params: { id: string } }) {
  return (
    <InvitationGate invitationId={params.id}>
      <FeedbackLayout>
        <FeedbackFormPage invitationId={params.id} />
      </FeedbackLayout>
    </InvitationGate>
  )
}

// Unguarded for the same reason as SessionListRoute.
function KioskSessionRoute({ params }: { params: { sessionId: string } }) {
  return <SessionParticipantsPage params={params} />
}

/**
 * The surface a client meets — their own phone, or a kiosk tablet fixed in
 * portrait — so the rotate prompt is part of it.
 *
 * It sits here rather than over the whole app because the staff screens are
 * used at a desk, where a tablet turned landscape gives them more room. Making
 * it the surface's own rather than a predicate over paths means a screen added
 * inside gets the prompt and one added outside does not, with no list to keep
 * in step with the routes.
 */
function ClientSurface({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <PortraitOnly />
    </>
  )
}

function KioskFeedbackRoute({ params }: { params: { id: string } }) {
  return (
    <InvitationGate invitationId={params.id} kioskMode>
      <Layout>
        <FeedbackFormPage invitationId={params.id} kioskMode />
      </Layout>
    </InvitationGate>
  )
}

export function App() {
  return (
    <>
      <EnvBanner label={import.meta.env.VITE_ENV_BANNER} />
      <Router>
        <ErrorBoundary>
          <Switch>
            {/* Screens used at a desk. An administrator signs in here and
                works in whichever orientation suits, so these sit outside
                the portrait-only surface below. */}
            <Route path={paths.login}>
              <Layout>
                <LoginPage />
              </Layout>
            </Route>
            <Route path={paths.admin}>
              <Redirect to={paths.syncStatus} />
            </Route>
            <Route path={paths.syncStatus}>
              <AdminProtectedRoute>
                <SyncStatusPage />
              </AdminProtectedRoute>
            </Route>
            <Route path={paths.serviceTypes}>
              <AdminProtectedRoute>
                <ServiceTypesListPage />
              </AdminProtectedRoute>
            </Route>
            <Route path={paths.programQuestions}>
              <AdminProtectedRoute>
                <ProgramQuestionsPage />
              </AdminProtectedRoute>
            </Route>
            <Route path={paths.programConfig}>
              <AdminProtectedRoute>
                <ProgramConfigPage />
              </AdminProtectedRoute>
            </Route>
            <Route path={paths.rollout}>
              <AdminProtectedRoute>
                <RolloutPage />
              </AdminProtectedRoute>
            </Route>
            <Route path={paths.kioskDevices}>
              <AdminProtectedRoute>
                <KioskDevicesListPage />
              </AdminProtectedRoute>
            </Route>
            <Route path={paths.modules}>
              <AdminProtectedRoute wide>
                <ModulesListPage />
              </AdminProtectedRoute>
            </Route>
            <Route path={paths.newModule}>
              <AdminProtectedRoute wide>
                <NewModulePage />
              </AdminProtectedRoute>
            </Route>
            <Route path={paths.sessionTemplates}>
              <AdminProtectedRoute wide>
                <SessionTemplatesListPage />
              </AdminProtectedRoute>
            </Route>
            {/* Declared before :id so "new" is a screen, not an id. */}
            <Route path={paths.newSessionTemplate}>
              <AdminProtectedRoute wide>
                <NewSessionTemplatePage />
              </AdminProtectedRoute>
            </Route>
            <Route path={paths.sessionTemplate}>
              {(params) => (
                <AdminProtectedRoute wide>
                  <SessionTemplatePage id={params.id} />
                </AdminProtectedRoute>
              )}
            </Route>
            <Route path={paths.module}>
              {(params) => (
                <AdminProtectedRoute wide>
                  <ModuleCanvasPage id={params.id} />
                </AdminProtectedRoute>
              )}
            </Route>
            {/* /admin/forms is the nav's link and redirects itself to the first
                form; /admin/forms/:formId is what a person can bookmark. */}
            <Route path={paths.formBuilder}>
              <AdminProtectedRoute>
                <Suspense fallback={null}>
                  <FormBuilderPage />
                </Suspense>
              </AdminProtectedRoute>
            </Route>
            <Route path={paths.formBuilderForm}>
              {(params) => (
                <AdminProtectedRoute>
                  <Suspense fallback={null}>
                    <FormBuilderPage formId={params.formId} />
                  </Suspense>
                </AdminProtectedRoute>
              )}
            </Route>

            {/* Everything else is a screen a client meets, on their own
                phone or on a kiosk tablet fixed in portrait — including the
                404, which a mistyped link reaches. */}
            <Route>
              <ClientSurface>
                <Switch>
                  <Route path={paths.thankYou}>
                    <ThankYouPage />
                  </Route>
                  <Route
                    path={paths.feedbackWelcome}
                    component={FeedbackWelcomeRoute}
                  />
                  <Route
                    path={paths.feedbackForm}
                    component={FeedbackFormRoute}
                  />
                  {/* Public and unguarded, like the invitation routes: a participant
                    arrives from a QR code with no credential of any kind
                    (ADR-029 D1). One fixed address, carrying nothing — the code is
                    printed and put on a wall, so it cannot name a session. */}
                  <Route path={paths.challenge}>
                    <IdentityChallengePage />
                  </Route>
                  {/* Ungated: guarding it would redirect to itself. */}
                  <Route path={paths.kioskLogin}>
                    <Layout>
                      <KioskLoginPage />
                    </Layout>
                  </Route>
                  <Route path={paths.kioskWelcome}>
                    <KioskProtectedRoute>
                      <KioskWelcomePage />
                    </KioskProtectedRoute>
                  </Route>
                  <Route
                    path={paths.kioskSessions}
                    component={SessionListRoute}
                  />
                  <Route
                    path={paths.kioskSession}
                    component={KioskSessionRoute}
                  />
                  {/* Ungated: a lost credential must not throw a client out of a
                    part-finished form. */}
                  <Route
                    path={paths.kioskFeedback}
                    component={KioskFeedbackRoute}
                  />
                  <Route path={paths.kioskLocation}>
                    <Layout fill>
                      {/* Staff-guarded, but a kiosk route: an unauthenticated tap
                        belongs on the kiosk's sign-in screen, not the admin one. */}
                      <ProtectedRoute loginPath={paths.kioskLogin}>
                        <LocationSettingsPage />
                      </ProtectedRoute>
                    </Layout>
                  </Route>
                  {import.meta.env.VITE_APP_ENV !== "production" && (
                    <Route path={paths.diagnostics}>
                      <Layout>
                        <DiagnosticsPage />
                      </Layout>
                    </Route>
                  )}
                  <Route component={NotFoundPage} />
                </Switch>
              </ClientSurface>
            </Route>
          </Switch>
        </ErrorBoundary>
      </Router>
    </>
  )
}
